// A small OpenEXR reader for depth maps: scanline or tiled (one level), no compression, ZIPS or ZIP,
// half or float channels. That covers what 3ds Max and Corona write by default. three.js's EXRLoader
// can't read tiled files, which is what Max saves, hence this. No dependencies: it uses the browser's
// DecompressionStream for zlib.

export interface ExrImage {
  width: number;
  height: number;
  /** Channel names in the file, e.g. ['A', 'B', 'G', 'R'] or ['Z']. */
  channels: string[];
  /** Pixels of one channel (see pickDepthChannel), rows top first as stored. */
  data: Float32Array;
  /** The channel read. */
  channel: string;
  /** 3ds Max writes the camera's transform (a 4x4, row vectors, scene units). */
  cameraTransform: number[] | null;
}

interface Attr { type: string; bytes: Uint8Array }

const td = new TextDecoder();

function readStr(b: Uint8Array, i: number): [string, number] {
  let n = i;
  while (b[n] !== 0) n++;
  return [td.decode(b.subarray(i, n)), n + 1];
}

/** Half float bits to a number. */
function half(h: number): number {
  const s = h & 0x8000 ? -1 : 1, e = (h >> 10) & 0x1f, f = h & 0x3ff;
  if (e === 0) return s * 5.960464477539063e-8 * f;
  if (e === 31) return f ? NaN : s * Infinity;
  return s * Math.pow(2, e - 15) * (1 + f / 1024);
}

async function inflate(data: Uint8Array): Promise<Uint8Array> {
  const ds = new DecompressionStream('deflate');
  const out = new Response(new Blob([data as BlobPart]).stream().pipeThrough(ds));
  return new Uint8Array(await out.arrayBuffer());
}

/** Undo EXR's ZIP filter: byte predictor, then the two halves interleaved. */
function unfilter(t: Uint8Array): Uint8Array {
  for (let i = 1; i < t.length; i++) t[i] = (t[i - 1] + t[i] - 128) & 0xff;
  const out = new Uint8Array(t.length);
  const half = (t.length + 1) >> 1;
  for (let i = 0, k = 0; k < t.length; i++) {
    out[k++] = t[i];
    if (k < t.length) out[k++] = t[half + i];
  }
  return out;
}

/** Which channel holds the depth: Z if there is one, else R, else the first. */
export function pickDepthChannel(channels: string[]): string {
  const find = (n: string) => channels.find((c) => c === n || c.endsWith('.' + n));
  return find('Z') ?? find('R') ?? find('Y') ?? channels[0];
}

export async function readExr(buf: ArrayBuffer, want?: string): Promise<ExrImage> {
  const b = new Uint8Array(buf);
  const dv = new DataView(buf);
  if (dv.getUint32(0, true) !== 20000630) throw new Error('Not an EXR file');
  const flags = dv.getUint32(4, true);
  const tiled = !!(flags & 0x200);
  if (flags & 0x1800) throw new Error('Deep or multi-part EXR files aren’t supported');
  let i = 8;
  const attrs = new Map<string, Attr>();
  while (b[i] !== 0) {
    let name: string, type: string;
    [name, i] = readStr(b, i);
    [type, i] = readStr(b, i);
    const size = dv.getInt32(i, true); i += 4;
    attrs.set(name, { type, bytes: b.subarray(i, i + size) });
    i += size;
  }
  i++;
  const at = (n: string) => { const a = attrs.get(n); if (!a) throw new Error(`EXR is missing ${n}`); return a; };
  const av = (a: Attr) => new DataView(a.bytes.buffer, a.bytes.byteOffset, a.bytes.byteLength);

  // channels: name, pixel type (0 uint, 1 half, 2 float), sampling
  const chs: Array<{ name: string; type: number; size: number }> = [];
  { const a = at('channels'); const v = av(a); let j = 0;
    while (a.bytes[j] !== 0) { let n: string; [n, j] = readStr(a.bytes, j); const t = v.getInt32(j, true); chs.push({ name: n, type: t, size: t === 1 ? 2 : 4 }); j += 16; } }
  const comp = at('compression').bytes[0];
  if (![0, 2, 3].includes(comp)) throw new Error('This EXR compression isn’t supported yet: save it with ZIP or no compression');
  const dw = av(at('dataWindow'));
  const x0 = dw.getInt32(0, true), y0 = dw.getInt32(4, true), x1 = dw.getInt32(8, true), y1 = dw.getInt32(12, true);
  const W = x1 - x0 + 1, H = y1 - y0 + 1;
  const name = want ?? pickDepthChannel(chs.map((c) => c.name));
  const ci = chs.findIndex((c) => c.name === name);
  if (ci < 0) throw new Error(`EXR has no ${name} channel`);
  const pixBytes = chs.reduce((s, c) => s + c.size, 0);
  const out = new Float32Array(W * H);

  let cam: number[] | null = null;
  const ct = attrs.get('cameraTransform');
  if (ct && ct.type === 'm44f' && ct.bytes.byteLength >= 64) { const v = av(ct); cam = []; for (let k = 0; k < 16; k++) cam.push(v.getFloat32(k * 4, true)); }

  // copy one block of rows (bx..bx+bw, by..by+bh) out of uncompressed bytes laid out line by line, channel by channel
  const take = (raw: Uint8Array, bx: number, by: number, bw: number, bh: number) => {
    const rv = new DataView(raw.buffer, raw.byteOffset, raw.byteLength);
    let off = 0;
    for (let y = 0; y < bh; y++) {
      for (let c = 0; c < chs.length; c++) {
        const ch = chs[c];
        if (c === ci) {
          const row = (by + y) * W + bx;
          for (let x = 0; x < bw; x++) {
            const p = off + x * ch.size;
            out[row + x] = ch.type === 1 ? half(rv.getUint16(p, true)) : ch.type === 2 ? rv.getFloat32(p, true) : rv.getUint32(p, true);
          }
        }
        off += bw * ch.size;
      }
    }
  };
  const decode = async (data: Uint8Array, expect: number) => (comp === 0 || data.length === expect ? data : unfilter(await inflate(data)));

  if (tiled) {
    const t = av(at('tiles'));
    const tw = t.getUint32(0, true), th = t.getUint32(4, true);
    if ((t.getUint8(8) & 0xf) !== 0) throw new Error('Mipmapped EXR tiles aren’t supported');
    const nx = Math.ceil(W / tw), ny = Math.ceil(H / th), n = nx * ny;
    const offs: number[] = [];
    for (let k = 0; k < n; k++) offs.push(Number(dv.getBigUint64(i + k * 8, true)));
    for (const o of offs) {
      const tx = dv.getInt32(o, true), ty = dv.getInt32(o + 4, true);
      const size = dv.getInt32(o + 16, true);
      const bx = tx * tw, by = ty * th;
      const bw = Math.min(tw, W - bx), bh = Math.min(th, H - by);
      const raw = await decode(b.subarray(o + 20, o + 20 + size), bw * bh * pixBytes);
      take(raw, bx, by, bw, bh);
    }
  } else {
    const lines = comp === 3 ? 16 : 1;
    const n = Math.ceil(H / lines);
    for (let k = 0; k < n; k++) {
      const o = Number(dv.getBigUint64(i + k * 8, true));
      const y = dv.getInt32(o, true) - y0, size = dv.getInt32(o + 4, true);
      const bh = Math.min(lines, H - y);
      const raw = await decode(b.subarray(o + 8, o + 8 + size), W * bh * pixBytes);
      take(raw, 0, y, W, bh);
    }
  }
  return { width: W, height: H, channels: chs.map((c) => c.name), data: out, channel: name, cameraTransform: cam };
}
